import { Navigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import { getUserInfo } from "services/authService";
import { getAccessToken } from "services/tokenStorage";

/**
 * Route guard for the portal's pages.
 *
 * - Not signed in (no user profile or no access token) → sign-in page.
 * - Signed in but the page is not for this role → dashboard.
 *
 * Read on every render rather than cached, so it picks up the user written to
 * localStorage by the sign-in page without a reload. This only decides what to
 * render — the backend's tokenVerify + authorize middleware are the real gate.
 */
function ProtectedRoute({ roles, children }) {
  const location = useLocation();
  const user = getUserInfo();

  if (!user || !getAccessToken()) {
    return <Navigate to="/authentication/sign-in" replace state={{ from: location }} />;
  }

  if (roles && !roles.includes(user.role)) {
    // A role that cannot even see the dashboard would bounce here forever.
    const fallback = location.pathname === "/dashboard" ? "/authentication/sign-in" : "/dashboard";
    return <Navigate to={fallback} replace />;
  }

  return children;
}

ProtectedRoute.defaultProps = {
  roles: null,
};

ProtectedRoute.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node.isRequired,
};

export default ProtectedRoute;
